import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";

import { api } from "../convex_api";

type AdminRole = "admin" | "user" | "worker";

// Human-readable role labels (what the user sees instead of the raw role).
const ROLE_LABEL: Record<AdminRole, string> = {
  admin: "Admin",
  user: "Acquisitions Team Member",
  worker: "Worker Service Account",
};

// Greeting fragment shown after the role label.
const ROLE_GREETING: Record<AdminRole, string> = {
  admin: "with full access to the CCLP Acquisitions Agent",
  user: "with team-member access to the CCLP Acquisitions Agent",
  worker: "with worker-service access to the CCLP Acquisitions Agent",
};

// Scope chip catalogue. Order here is the render order.
const SCOPE_CHIPS: { scope: string; label: string }[] = [
  { scope: "mcp:tools", label: "Read-only property data" },
  { scope: "mcp:user", label: "Edit property data + dispatch agents" },
  {
    scope: "mcp:worker",
    label: "Worker write-back tools (zoning + synthesis writes)",
  },
];

export function OAuthConsentRoute() {
  return (
    <>
      <SignedOut>
        <main className="shell">
          <section className="card">
            <p className="eyebrow">CCLP Acquisitions Agent</p>
            <h1>Sign in to authorize this connection.</h1>
            <p>
              You're being asked to authorize a connection to the CCL
              Acquisitions Agent. Please sign in first; you'll return to
              this consent screen automatically.
            </p>
            <div style={{ marginTop: 20 }}>
              <SignInButton mode="modal">
                <button className="primary">Sign in</button>
              </SignInButton>
            </div>
          </section>
        </main>
      </SignedOut>
      <SignedIn>
        <ConsentSurface />
      </SignedIn>
    </>
  );
}

function ConsentSurface() {
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<"idle" | "approving" | "denying" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const createAuthCode = useMutation(api.oauth.createAuthCode);
  const entitlements = useQuery(api.oauth.getMyEntitlements, {});

  const clientId = params.get("client_id") ?? "";
  const redirectUri = params.get("redirect_uri") ?? "";
  const codeChallenge = params.get("code_challenge") ?? "";
  const codeChallengeMethod = params.get("code_challenge_method") ?? "";
  const state = params.get("state");
  const scopeRaw = params.get("scope") ?? "mcp:tools";
  const requestedScope = scopeRaw.split(/\s+/).filter(Boolean);
  const clientName = params.get("client_name") ?? "An MCP client";

  const missing: string[] = [];
  if (!clientId) missing.push("client_id");
  if (!redirectUri) missing.push("redirect_uri");
  if (!codeChallenge) missing.push("code_challenge");
  if (codeChallengeMethod !== "S256") missing.push("code_challenge_method=S256");

  // Preserve registered redirect_uri byte-for-byte.
  function buildRedirect(extra: Record<string, string>): string {
    const queryParams = new URLSearchParams();
    for (const [k, v] of Object.entries(extra)) queryParams.set(k, v);
    if (state) queryParams.set("state", state);
    const sep = redirectUri.includes("?") ? "&" : "?";
    return `${redirectUri}${sep}${queryParams.toString()}`;
  }

  // Pre-filter scopes to what user is entitled to (server enforces too — defense in depth)
  const allowedScopes: string[] = entitlements?.allowed_scopes ?? [];
  const grantableScope = requestedScope.filter((s) => allowedScopes.includes(s));
  const droppedScope = requestedScope.filter((s) => !allowedScopes.includes(s));

  async function handleApprove() {
    setPhase("approving");
    setError(null);
    try {
      const result = await createAuthCode({
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod,
        scope: grantableScope,
        state: state ?? undefined,
      });
      window.location.href = buildRedirect({ code: result.code });
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : "Could not complete consent.");
    }
  }

  function handleDeny() {
    setPhase("denying");
    try {
      window.location.href = buildRedirect({ error: "access_denied" });
    } catch {
      setPhase("error");
      setError("Could not redirect back to the client.");
    }
  }

  if (missing.length > 0) {
    return (
      <main className="shell">
        <section className="card">
          <p className="eyebrow eyebrow-danger">Something's missing from this link</p>
          <h1>This connection link looks incomplete.</h1>
          <p>
            Return to your AI client (Claude Desktop, Code, or Cowork) and
            start the connection again. If this keeps happening, ping the team.
          </p>
        </section>
      </main>
    );
  }

  if (entitlements === undefined) {
    return (
      <main className="shell">
        <section className="card">
          <p className="eyebrow">CCLP Acquisitions Agent</p>
          <h1>One moment…</h1>
        </section>
      </main>
    );
  }

  if (entitlements?.reason === "user_disabled_or_unknown") {
    return (
      <main className="shell">
        <section className="card">
          <p className="eyebrow eyebrow-danger">Account not yet set up</p>
          <h1>You're not on the CCLP Acquisitions Agent access list yet.</h1>
          <p>
            Ask Mufaro or an admin to add you. Once they do, you can come back
            here and authorize the connection.
          </p>
        </section>
      </main>
    );
  }

  if (entitlements?.reason === "role_disabled" || entitlements?.enabled === false) {
    return (
      <main className="shell">
        <section className="card">
          <p className="eyebrow eyebrow-danger">Access temporarily paused</p>
          <h1>
            Hi {entitlements.name?.split(" ")[0] ?? "there"} — your access is currently paused.
          </h1>
          <p>Ping Mufaro to re-enable.</p>
        </section>
      </main>
    );
  }

  if (grantableScope.length === 0) {
    return (
      <main className="shell">
        <section className="card">
          <p className="eyebrow eyebrow-danger">Nothing to authorize</p>
          <h1>This connection isn't asking for anything you can grant.</h1>
          <p>
            Cancel and try again from your AI client. If this keeps happening,
            ping the team.
          </p>
        </section>
      </main>
    );
  }

  const role: AdminRole = (entitlements.role as AdminRole) ?? "user";
  const firstName = entitlements.name?.split(" ")[0];
  // Prefer the user's display_title (e.g. "Technical Lead / COO") over the
  // entitlement role label, since role is internal categorization and doesn't
  // always match the user's actual title.
  const displayTitle = entitlements.display_title?.trim() || ROLE_LABEL[role] || role;
  const greeting = ROLE_GREETING[role] ?? ROLE_GREETING.user;

  const grantableChips = SCOPE_CHIPS.filter((c) => grantableScope.includes(c.scope));

  return (
    <main className="shell">
      <section className="card">
        <p className="eyebrow">CCLP Acquisitions Agent</p>
        <h1>
          {firstName ? `Hi ${firstName} — ` : ""}
          {clientName} wants to connect to the CCLP Acquisitions Agent.
        </h1>
        <p>
          You'll be approving as the {displayTitle}, {greeting}.
          You can revoke this connection at any time from{" "}
          <a href="/connections">Settings → Connections</a>.
        </p>

        <div className="permissions">
          <h2>What {clientName} will be able to do</h2>
          <ul className="scope-list">
            {grantableChips.map((c) => (
              <li key={c.scope}>✓ {c.label}</li>
            ))}
          </ul>
          {droppedScope.length > 0 && (
            <p className="scope-warn">
              Some requested permissions aren't available to your role and have
              been dropped: {droppedScope.map((s) => SCOPE_CHIPS.find((c) => c.scope === s)?.label ?? s).join(", ")}.
            </p>
          )}
        </div>

        {error && <p className="error">{error}</p>}

        <div className="actions">
          <button
            type="button"
            onClick={handleDeny}
            disabled={phase === "approving" || phase === "denying"}
          >
            {phase === "denying" ? "Returning…" : "Deny"}
          </button>
          <button
            type="button"
            className="primary"
            onClick={handleApprove}
            disabled={phase === "approving" || phase === "denying"}
          >
            {phase === "approving" ? "Approving…" : "Approve"}
          </button>
        </div>

        <p className="hint" style={{ marginTop: 18 }}>
          Approving issues a short-lived authorization code. Your access token
          stays with your AI client and never reaches CCL servers.
        </p>
        <p className="hint" style={{ marginTop: 8 }}>
          Documentation forthcoming.
        </p>
      </section>
    </main>
  );
}
